import React, {
  createContext,
  useCallback,
  useContext,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { Box, Paper, Typography } from "@mui/material";

/**
 * undefined  → not wrapped in ReportPageLayout (caller must keep actions visible)
 * null       → wrapped, toolbar slot not mounted yet (suppress to avoid wrong-place flash)
 * HTMLElement → portal target in the title row
 *
 * The title-row slot is shared: every <ReportToolbar> portals into the same node.
 * If more than one mounts, children appear in mount order (do not rely on more than one).
 */
const ReportToolbarTargetContext = createContext(undefined);

/**
 * Title row (left) + toolbar slot (right), then page body in Paper.
 * Export/actions from report bodies should render through <ReportToolbar>.
 * Manage list screens use <PageToolbarRow> for the same portal slot.
 */
export function ReportPageLayout({
  title,
  classes,
  children,
  showHeader = true,
  paperProps = {},
}) {
  const [toolbarNode, setToolbarNode] = useState(null);

  const setToolbarRef = useCallback((node) => {
    setToolbarNode((prev) => (prev === node ? prev : node));
  }, []);

  const {
    className: paperClassName,
    elevation: paperElevation = 1,
    sx: paperSx,
    ...restPaperProps
  } = paperProps;

  return (
    <ReportToolbarTargetContext.Provider value={toolbarNode}>
      <div id="react-no-print" className={classes.main}>
        {showHeader ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              marginLeft: "1rem",
              marginRight: "1rem",
              minHeight: 44,
            }}
          >
            <Typography
              variant="display1"
              className={classes.typography}
              sx={{
                marginLeft: "0 !important",
                marginBottom: 0,
                flex: 1,
                minWidth: 0,
              }}
            >
              {title}
            </Typography>
            <Box
              ref={setToolbarRef}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                flexShrink: 0,
                flexWrap: "wrap",
                gap: 1,
                maxWidth: "100%",
              }}
            />
          </Box>
        ) : null}
        {/*
          Paper is intentional App chrome: most report/manage bodies do not wrap themselves.
          marginTop tightened vs classes.paper so the title+toolbar row sits closer to the grid.
        */}
        <Paper
          className={paperClassName != null ? paperClassName : classes.paper}
          elevation={paperElevation}
          sx={{ marginTop: showHeader ? "8px" : undefined, ...paperSx }}
          {...restPaperProps}
        >
          {children}
        </Paper>
      </div>
    </ReportToolbarTargetContext.Provider>
  );
}

/**
 * Places export/actions in the report title row when under ReportPageLayout.
 * If rendered outside that layout, falls back to inline so actions never disappear.
 * Multiple instances share one slot and append in mount order — use a single toolbar per page.
 */
export function ReportToolbar({ children }) {
  const target = useContext(ReportToolbarTargetContext);

  if (target === undefined) {
    return <React.Fragment>{children}</React.Fragment>;
  }

  // First render under ReportPageLayout: ref callback has not run yet, so target is null.
  // Returning null here avoids a one-frame flash of actions in the body; the next render portals.
  if (!target) {
    return null;
  }

  return createPortal(children, target);
}

export default ReportPageLayout;



// WEBPACK FOOTER //
// ./src/components/reports/ReportPageLayout.js